import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../lib/utils'

/**
 * Bridge component for code migrating from Joy's <Typography level=...>.
 * Each `level` maps 1:1 to the Joy levels we used across axilon-devops and
 * axilon-ai-desktop, so callers can swap `<Typography level="title-md">` for
 * `<Text level="title-md">` without re-thinking the visual hierarchy.
 *
 * Defaults to a `<span>` so it can be inlined safely; pass `as="..."` to
 * render a heading/paragraph element when semantics matter.
 */

const textVariants = cva('', {
  variants: {
    level: {
      h1: 'text-3xl font-semibold tracking-tight leading-tight',
      h2: 'text-2xl font-semibold tracking-tight leading-tight',
      h3: 'text-xl font-semibold tracking-tight leading-snug',
      h4: 'text-lg font-semibold leading-snug',
      'title-lg': 'text-base font-semibold',
      'title-md': 'text-sm font-semibold',
      'title-sm': 'text-xs font-semibold uppercase tracking-wide',
      'body-lg': 'text-base',
      'body-md': 'text-sm',
      'body-sm': 'text-xs',
      'body-xs': 'text-[0.6875rem] leading-tight'
    },
    color: {
      default: 'text-foreground',
      muted: 'text-muted-foreground',
      primary: 'text-primary',
      danger: 'text-destructive',
      warning: 'text-st-warn',
      success: 'text-st-run',
      inherit: ''
    },
    truncate: {
      true: 'truncate',
      false: ''
    }
  },
  defaultVariants: {
    level: 'body-md',
    color: 'default',
    truncate: false
  }
})

type AsProp =
  | 'span'
  | 'p'
  | 'div'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'label'
  | 'small'
  | 'strong'
  | 'pre'

type TextLevel = NonNullable<VariantProps<typeof textVariants>['level']>

type TextProps = Omit<React.HTMLAttributes<HTMLElement>, 'color'> &
  Omit<VariantProps<typeof textVariants>, 'level'> & {
    as?: AsProp
    /**
     * Typography level. Both names accepted:
     *  - `variant` — shadcn-flavoured name used at most Maestro call sites
     *  - `level` — Joy-compat alias from the migration bridge
     * If both are provided, `variant` wins.
     */
    variant?: TextLevel
    level?: TextLevel
  }

const levelDefaultElement: Partial<Record<TextLevel, AsProp>> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4'
}

const Text = React.forwardRef<HTMLElement, TextProps>(function Text(
  { as, level, variant, color, truncate, className, ...props },
  ref
) {
  const resolvedLevel: TextLevel = variant ?? level ?? 'body-md'
  const Component = (as ?? levelDefaultElement[resolvedLevel] ?? 'span') as AsProp
  return React.createElement(Component, {
    ref,
    'data-slot': 'text',
    'data-level': resolvedLevel,
    className: cn(textVariants({ level: resolvedLevel, color, truncate }), className),
    ...props
  })
})

export { Text, textVariants, type TextProps }
